import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Select,
  FormControl,
  FormLabel,
  useToast,
  Flex,
  IconButton,
  Tag,
  Progress,
  Divider,
} from "@chakra-ui/react";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiBarChart,
  FiCalendar,
  FiUsers,
  FiClock,
  FiDownload,
  FiFilter,
  FiSearch,
  FiX,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";
import { useOvertimeAnalytics, useAdminOvertimeAnalytics, overtimeUtils } from "../../services/overtimeService";
import { usePlanRestriction } from "../../context/PlanRestrictionContext";
import { useUserContext } from "../../context/UserContext";
import { numericFormat } from "../../utils";
import BranchFilter from "../../components/BranchFilter";

const OvertimeReports = ({ selectedBranch: propSelectedBranch }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(propSelectedBranch || 'all');
  
  const [filters, setFilters] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    overtime_type: 'all',
    employee_id: '',
    department: '',
    // Show all data by default, only filter when specific branch is selected
    ...(propSelectedBranch && propSelectedBranch !== 'all' && propSelectedBranch !== undefined && { branch_id: propSelectedBranch })
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  
  const { canAccessFeature, showFeatureRestriction } = usePlanRestriction();
  const { user } = useUserContext();
  
  // Use admin endpoints for company admins, regular endpoints for others
  const isCompanyAdmin = user?.role === 'company_admin';
  
  // Call both hooks unconditionally to follow Rules of Hooks
  const adminOvertimeData = useAdminOvertimeAnalytics({
    enabled: isCompanyAdmin,
    filters,
    branch_id: selectedBranch || 'all' // Pass branch_id for admin filtering, default to 'all' if undefined
  });
  
  const regularOvertimeData = useOvertimeAnalytics({
    enabled: !isCompanyAdmin,
    filters
  });
  
  // Select the appropriate data based on user role
  const {
    summaryData,
    reportsData,
    summaryIsLoading,
    reportsIsLoading,
    hasError,
    refetchAll
  } = isCompanyAdmin ? adminOvertimeData : regularOvertimeData;

  // Sync local selectedBranch with prop when it changes
  useEffect(() => {
    if (propSelectedBranch !== undefined) {
      setSelectedBranch(propSelectedBranch);
    }
  }, [propSelectedBranch]);

  // Update filters when selectedBranch changes
  useEffect(() => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (selectedBranch && selectedBranch !== 'all' && selectedBranch !== undefined) {
        newFilters.branch_id = selectedBranch;
      } else {
        delete newFilters.branch_id; // Remove branch filter to show all data
      }
      return newFilters;
    });
  }, [selectedBranch]);



  // Filter reports based on search term
  const filteredReports = useMemo(() => {
    if (!reportsData?.length) return [];
    
    return reportsData.filter(report => 
      report.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.department?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [reportsData, searchTerm]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!summaryData) return {};
    
    return {
      totalEmployees: summaryData.total_employees || 0,
      totalOvertimeHours: summaryData.total_overtime_hours || 0,
      earlyClockinHours: summaryData.early_clockin_hours || 0,
      lateClockoutHours: summaryData.late_clockout_hours || 0,
      fullOvertimeHours: summaryData.full_overtime_hours || 0,
      complianceRate: summaryData.shift_compliance_rate || 0,
      dateRange: summaryData.date_range || 'Current Period'
    };
  }, [summaryData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      overtime_type: 'all',
      employee_id: '',
      department: '',
      // Show all data by default, only filter when specific branch is selected
      ...(selectedBranch && selectedBranch !== 'all' && selectedBranch !== undefined && { branch_id: selectedBranch })
    });
    setSearchTerm("");
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    toast({
      title: "Export Feature",
      description: "Export functionality will be implemented soon.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Feature access is now only restricted by user role (branch vs main branch)
  // All Main Branch users have access to Overtime Reports regardless of plan

  if (hasError) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="red.100"
            color="red.500"
            fontSize="2xl"
          >
            <FiAlertTriangle />
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              Error Loading Overtime Reports
            </Text>
            <Text color="gray.500" fontSize="sm">
              The overtime reports backend endpoints are not yet available. Please check with your administrator.
            </Text>
            <Text color="gray.400" fontSize="xs">
              Expected endpoints: /admin/overtime/reports, /admin/overtime/summary
            </Text>
            <Button onClick={refetchAll} colorScheme="blue" size="sm">
              Retry
            </Button>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box p={6}>
             {/* Header */}
       <Flex justifyContent="space-between" alignItems="center" mb={6}>
         <VStack align="start" spacing={1}>
           <Heading size="lg" color="gray.900" fontWeight="700">
             Overtime Reports
           </Heading>
           <Text color="gray.600" fontSize="sm">
             Comprehensive overtime analytics and reporting
           </Text>
         </VStack>
         <HStack spacing={3}>
           {isCompanyAdmin && (
             <BranchFilter
               selectedBranch={selectedBranch}
               onBranchChange={setSelectedBranch}
             />
           )}
           <Button
             leftIcon={<FiFilter />}
             variant="outline"
             onClick={onOpen}
             size="sm"
           >
             Filters
           </Button>
           <Button
             leftIcon={<FiDownload />}
             colorScheme="blue"
             onClick={handleExport}
             size="sm"
           >
             Export
           </Button>
         </HStack>
       </Flex>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <SummaryCard
          title="Total Employees"
          value={summaryStats.totalEmployees}
          icon={<FiUsers />}
          color="blue"
          isLoading={summaryIsLoading}
        />
        <SummaryCard
          title="Total Overtime Hours"
          value={`${summaryStats.totalOvertimeHours.toFixed(2)}h`}
          icon={<FiClock />}
          color="red"
          isLoading={summaryIsLoading}
        />
        <SummaryCard
          title="Compliance Rate"
          value={`${summaryStats.complianceRate.toFixed(1)}%`}
          icon={<FiCheckCircle />}
          color="green"
          isLoading={summaryIsLoading}
        />
        <SummaryCard
          title="Date Range"
          value={summaryStats.dateRange}
          icon={<FiCalendar />}
          color="purple"
          isLoading={summaryIsLoading}
        />
      </SimpleGrid>

      {/* Overtime Breakdown */}
      <Card mb={8}>
        <CardBody>
          <Heading size="md" mb={4}>Overtime Breakdown</Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <OvertimeBreakdownCard
              title="Early Clock-in"
              hours={summaryStats.earlyClockinHours}
              color="blue"
              isLoading={summaryIsLoading}
            />
            <OvertimeBreakdownCard
              title="Late Clock-out"
              hours={summaryStats.lateClockoutHours}
              color="orange"
              isLoading={summaryIsLoading}
            />
            <OvertimeBreakdownCard
              title="Full Overtime"
              hours={summaryStats.fullOvertimeHours}
              color="red"
              isLoading={summaryIsLoading}
            />
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardBody>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Heading size="md">Overtime Records</Heading>
            <HStack spacing={2}>
              <Box position="relative">
                <Input
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  pl={10}
                  pr={searchTerm ? 10 : 4}
                  size="sm"
                  w="300px"
                />
                <Box position="absolute" left={3} top="50%" transform="translateY(-50%)" color="gray.400">
                  <FiSearch size={16} />
                </Box>
                {searchTerm && (
                  <Box 
                    position="absolute" 
                    right={3} 
                    top="50%" 
                    transform="translateY(-50%)" 
                    cursor="pointer"
                    onClick={() => setSearchTerm("")}
                    color="gray.400"
                    _hover={{ color: "gray.600" }}
                  >
                    <FiX size={16} />
                  </Box>
                )}
              </Box>
            </HStack>
          </Flex>

          {reportsIsLoading ? (
            <Box p={8} textAlign="center">
              <Text>Loading overtime records...</Text>
            </Box>
          ) : filteredReports.length === 0 ? (
            <Box p={8} textAlign="center">
              <VStack spacing={4}>
                <Box
                  p={4}
                  borderRadius="50%"
                  bg="gray.100"
                  color="gray.500"
                  fontSize="2xl"
                >
                  📊
                </Box>
                <VStack spacing={2}>
                  <Text fontSize="lg" fontWeight="600" color="gray.700">
                    No Overtime Records
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    No overtime records found for the selected criteria
                  </Text>
                </VStack>
              </VStack>
            </Box>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Employee</Th>
                    <Th>Department</Th>
                    <Th>Date</Th>
                    <Th>Shift Hours</Th>
                    <Th>Overtime Hours</Th>
                    <Th>Overtime Type</Th>
                    <Th>Compliance</Th>
                    <Th>Grace Period</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredReports.map((report, index) => (
                    <OvertimeTableRow key={index} report={report} />
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter Overtime Reports</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              <SimpleGrid columns={2} spacing={4}>
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Overtime Type</FormLabel>
                <Select
                  value={filters.overtime_type}
                  onChange={(e) => handleFilterChange('overtime_type', e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="early_clockin">Early Clock-in</option>
                  <option value="late_clockout">Late Clock-out</option>
                  <option value="full_overtime">Full Overtime</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Employee ID</FormLabel>
                <Input
                  placeholder="Enter employee ID"
                  value={filters.employee_id}
                  onChange={(e) => handleFilterChange('employee_id', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Department</FormLabel>
                <Input
                  placeholder="Enter department"
                  value={filters.department}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                />
              </FormControl>

              <HStack spacing={3} pt={4}>
                <Button
                  colorScheme="blue"
                  variant="solid"
                  onClick={onClose}
                  size="md"
                  flex={1}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    clearFilters();
                    onClose();
                  }}
                  size="md"
                  flex={1}
                >
                  Clear All
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, icon, color, isLoading }) => {
  return (
    <Card>
      <CardBody>
        <Stat>
          <HStack spacing={3} mb={2}>
            <Box
              p={2}
              borderRadius="lg"
              bg={`${color}.100`}
              color={`${color}.600`}
            >
              {icon}
            </Box>
            <StatLabel color="gray.600" fontSize="sm">{title}</StatLabel>
          </HStack>
          {isLoading ? (
            <Box h="32px" bg="gray.200" borderRadius="md" />
          ) : (
            <StatNumber fontSize="2xl" fontWeight="700" color="gray.900">
              {value}
            </StatNumber>
          )}
        </Stat>
      </CardBody>
    </Card>
  );
};

// Overtime Breakdown Card Component
const OvertimeBreakdownCard = ({ title, hours, color, isLoading }) => {
  return (
    <Card>
      <CardBody>
        <VStack spacing={3} align="stretch">
          <Text fontSize="sm" fontWeight="600" color="gray.700">
            {title}
          </Text>
          {isLoading ? (
            <Box h="20px" bg="gray.200" borderRadius="md" />
          ) : (
            <Text fontSize="xl" fontWeight="700" color={`${color}.600`}>
              {hours.toFixed(2)}h
            </Text>
          )}
          <Progress
            value={hours > 0 ? Math.min((hours / 10) * 100, 100) : 0}
            colorScheme={color}
            size="sm"
            borderRadius="full"
          />
        </VStack>
      </CardBody>
    </Card>
  );
};

// Overtime Table Row Component
const OvertimeTableRow = ({ report }) => {
  return (
    <Tr>
      <Td>
        <VStack align="start" spacing={1}>
          <Text fontWeight="600">{report.full_name}</Text>
          <Text fontSize="sm" color="gray.500">{report.employee_id}</Text>
        </VStack>
      </Td>
      <Td>{report.department}</Td>
      <Td>{numericFormat(report.date)}</Td>
      <Td>{report.shift_hours_worked ? `${report.shift_hours_worked.toFixed(2)}h` : "-"}</Td>
      <Td>
        {report.overtime_hours > 0 ? (
          <Text color="red.600" fontWeight="600">
            {overtimeUtils.formatOvertimeHours(report.overtime_hours)}
          </Text>
        ) : (
          "-"
        )}
      </Td>
      <Td>
        {report.overtime_type && report.overtime_type !== 'none' ? (
          <Tag
            colorScheme={overtimeUtils.getOvertimeTypeColor(report.overtime_type)}
            variant="subtle"
            size="sm"
          >
            {overtimeUtils.getOvertimeTypeDisplay(report.overtime_type)}
          </Tag>
        ) : (
          "-"
        )}
      </Td>
      <Td>
        <Tag
          colorScheme={report.shift_compliance ? 'green' : 'red'}
          variant="subtle"
          size="sm"
        >
          {report.shift_compliance ? 'Compliant' : 'Non-compliant'}
        </Tag>
      </Td>
      <Td>
        {report.grace_period_used ? (
          <Text fontSize="sm" color="orange.600">
            Used
          </Text>
        ) : (
          <Text fontSize="sm" color="gray.500">
            Not used
          </Text>
        )}
      </Td>
    </Tr>
  );
};

export default OvertimeReports; 